const candidates = new WeakSet();
const overflowingUtilityTracks = new WeakSet();
const utilityMeasurements = new WeakMap();
const utilityTargets = new WeakMap();
const initializationPromises = new WeakMap();
const pendingTracks = new Set();
let observer;
let utilityResizeObserver;
let implementation;
let implementationPromise;
let fallbackTimer = 0;
let idleScheduled = false;
let pageLoaded = document.readyState === 'complete';
let pageEngaged = false;

function loadImplementation() {
    // Start both deferred chunks together; initialization then reuses the
    // package's native module cache instead of adding a second network round.
    implementationPromise ||= Promise.all([
        import('./blaze-slider.js'),
        import('blaze-slider'),
    ]).then(([module]) => {
        implementation = module;
        return module;
    });
    return implementationPromise;
}

function initializeTrack(track) {
    releaseUtilityMeasurement(track);
    pendingTracks.delete(track);
    const pending = initializationPromises.get(track);
    if (pending) return pending;
    const promise = loadImplementation()
        .then(({ initializeBlazeSlider }) => initializeBlazeSlider(track))
        .catch(() => {
            implementationPromise = undefined;
            // The native CSS rail remains the functional fallback.
        })
        .finally(() => {
            initializationPromises.delete(track);
        });
    initializationPromises.set(track, promise);
    return promise;
}

function initializePending() {
    idleScheduled = false;
    const tracks = [...pendingTracks];
    pendingTracks.clear();
    tracks.forEach((track) => {
        if (track.isConnected) void initializeTrack(track);
    });
}

function initializePendingWhenIdle() {
    if (idleScheduled || pendingTracks.size === 0) return;
    idleScheduled = true;
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(initializePending, { timeout: 1000 });
    } else {
        window.setTimeout(initializePending, 100);
    }
}

function scheduleFallback() {
    if (!pageLoaded || pageEngaged || fallbackTimer || pendingTracks.size === 0) {
        return;
    }
    // The native CSS rail is immediately usable. This late fallback enables
    // controls/autoplay for visitors who neither scroll nor otherwise interact.
    fallbackTimer = window.setTimeout(() => {
        fallbackTimer = 0;
        initializePendingWhenIdle();
    }, 10_000);
}

function markPageEngaged() {
    if (pageEngaged) return;
    pageEngaged = true;
    window.clearTimeout(fallbackTimer);
    fallbackTimer = 0;
    initializePendingWhenIdle();
}

function cancelUtilityMeasurement(measurements) {
    measurements.generation += 1;
    window.cancelAnimationFrame(measurements.frame);
    measurements.frame = 0;
    measurements.request?.bounds.forEach((_, target) => measurements.observer.unobserve(target));
    measurements.observer.takeRecords();
    measurements.request = null;
}

function releaseUtilityMeasurement(track) {
    const measurements = utilityMeasurements.get(track);
    if (!measurements) return;
    cancelUtilityMeasurement(measurements);
    measurements.observer.disconnect();
    measurements.mutationObserver.disconnect();
    window.removeEventListener('resize', measurements.onResize);
    measurements.targets.forEach((target) => {
        if (utilityTargets.get(target) !== track) return;
        utilityResizeObserver.unobserve(target);
        utilityTargets.delete(target);
    });
    utilityMeasurements.delete(track);
}

function syncUtilityTargets(track, measurements) {
    const targets = [track, ...track.children, ...track.querySelectorAll('[data-utility-content]')];
    measurements.targets.forEach((target) => {
        if (targets.includes(target) || utilityTargets.get(target) !== track) return;
        utilityResizeObserver.unobserve(target);
        utilityTargets.delete(target);
        measurements.widths.delete(target);
    });
    targets.forEach((target) => {
        if (utilityTargets.get(target) === track) return;
        utilityTargets.set(target, track);
        utilityResizeObserver.observe(target);
    });
    measurements.targets = targets;
}

function scheduleUtilityMeasurement(track) {
    const measurements = utilityMeasurements.get(track);
    if (!measurements) return;
    cancelUtilityMeasurement(measurements);
    measurements.frame = window.requestAnimationFrame(() => {
        measurements.frame = 0;
        if (!track.isConnected || track.dataset.blazeSlider !== 'utility') {
            releaseUtilityMeasurement(track);
            return;
        }
        syncUtilityTargets(track, measurements);
        const boxes = measurements.targets
            .slice(1)
            .filter((target) => measurements.widths.get(target) > 0);
        if (!boxes.length) return;
        measurements.request = {
            generation: measurements.generation,
            bounds: new Map(boxes.map((target) => [target, null])),
            rootBounds: null,
            rootTime: -1,
        };
        boxes.forEach((target) => measurements.observer.observe(target));
    });
}

function observeUtilityOverflow(track) {
    if (utilityMeasurements.has(track)) return;
    utilityResizeObserver ||= new ResizeObserver((entries) => {
        const changedTracks = new Set();
        entries.forEach((entry) => {
            const utilityTrack = utilityTargets.get(entry.target);
            if (!utilityMeasurements.has(utilityTrack)) return;
            if (!utilityTrack.isConnected) {
                releaseUtilityMeasurement(utilityTrack);
                return;
            }
            const box = entry.borderBoxSize?.[0] || entry.borderBoxSize;
            utilityMeasurements
                .get(utilityTrack)
                .widths.set(entry.target, box?.inlineSize ?? entry.contentRect.width);
            changedTracks.add(utilityTrack);
        });
        changedTracks.forEach(scheduleUtilityMeasurement);
    });
    const measurements = {
        targets: [],
        widths: new Map(),
        frame: 0,
        generation: 0,
        request: null,
        onResize: () => scheduleUtilityMeasurement(track),
        mutationObserver: new MutationObserver(() => scheduleUtilityMeasurement(track)),
        observer: new IntersectionObserver(
            (entries) => {
                if (!track.isConnected) {
                    releaseUtilityMeasurement(track);
                    return;
                }
                const request = measurements.request;
                if (!request || request.generation !== measurements.generation) return;
                for (const entry of entries) {
                    if (!request.bounds.has(entry.target)) continue;
                    if (!entry.rootBounds) {
                        cancelUtilityMeasurement(measurements);
                        return;
                    }
                    const previous = request.bounds.get(entry.target);
                    if (previous && previous.time > entry.time) continue;
                    request.bounds.set(entry.target, {
                        rect: entry.boundingClientRect,
                        time: entry.time,
                    });
                    if (entry.time >= request.rootTime) {
                        request.rootBounds = entry.rootBounds;
                        request.rootTime = entry.time;
                    }
                }
                if (!request.rootBounds || [...request.bounds.values()].some((bounds) => !bounds))
                    return;
                const rectangles = [...request.bounds.values()].map((bounds) => bounds.rect);
                // Real content boxes include text overhang, gaps and RTL order without style queries.
                const start = Math.min(...rectangles.map((rect) => rect.left));
                const end = Math.max(...rectangles.map((rect) => rect.right));
                const overflows =
                    request.rootBounds.width > 0 &&
                    (end - start > request.rootBounds.width + 1 ||
                        start < request.rootBounds.left - 1 ||
                        end > request.rootBounds.right + 1);
                cancelUtilityMeasurement(measurements);
                if (!overflows) return;
                releaseUtilityMeasurement(track);
                overflowingUtilityTracks.add(track);
                observe(track);
            },
            { root: track },
        ),
    };
    utilityMeasurements.set(track, measurements);
    measurements.mutationObserver.observe(track, { childList: true, subtree: true });
    window.addEventListener('resize', measurements.onResize, { passive: true });
    syncUtilityTargets(track, measurements);
}

function observe(track) {
    // Initialized roots retain the preset hook for styling. A later content
    // scan must not observe that wrapper as another uninitialized slide track.
    if (track.hasAttribute('data-blaze-ready') || candidates.has(track)) return;
    if (
        track.dataset.blazeSlider === 'utility' &&
        !overflowingUtilityTracks.has(track) &&
        typeof ResizeObserver !== 'undefined' &&
        typeof IntersectionObserver !== 'undefined'
    ) {
        // Check overflow after the native first layout. A short utility bar
        // stays observed without forcing layout or loading the Blaze chunk.
        observeUtilityOverflow(track);
        return;
    }
    candidates.add(track);
    const interactionRoot =
        track.dataset.blazeSlider === 'product-card-gallery'
            ? track.closest('.z-product-card') || track
            : track;
    const initializeForInteraction = () => {
        interactionRoot.removeEventListener('pointerenter', initializeForInteraction);
        interactionRoot.removeEventListener('pointerdown', initializeForInteraction);
        interactionRoot.removeEventListener('focusin', initializeForInteraction);
        void initializeTrack(track);
    };
    interactionRoot.addEventListener('pointerenter', initializeForInteraction, {
        passive: true,
    });
    interactionRoot.addEventListener('pointerdown', initializeForInteraction, {
        passive: true,
    });
    interactionRoot.addEventListener('focusin', initializeForInteraction);

    // Dozens of independent product-card galleries have no initial-load value.
    // Their native scroll-snap fallback remains active until the card is used.
    if (track.dataset.blazeSlider === 'product-card-gallery') return;

    if (typeof IntersectionObserver === 'undefined') {
        pendingTracks.add(track);
        if (pageEngaged) initializePendingWhenIdle();
        else scheduleFallback();
        return;
    }

    observer ||= new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                observer.unobserve(entry.target);
                pendingTracks.add(entry.target);
                if (pageEngaged) initializePendingWhenIdle();
                else scheduleFallback();
            });
        },
        { rootMargin: '600px 0px' },
    );
    observer.observe(track);
}

if (!pageLoaded) {
    window.addEventListener(
        'load',
        () => {
            pageLoaded = true;
            scheduleFallback();
        },
        { once: true },
    );
}

window.addEventListener('scroll', markPageEngaged, { once: true, passive: true });
window.addEventListener('pointerdown', markPageEngaged, {
    once: true,
    passive: true,
});
window.addEventListener('keydown', markPageEngaged, { once: true });

export function initBlazeSliders(root = document) {
    if (root.matches?.('[data-blaze-slider]')) observe(root);
    root.querySelectorAll?.('[data-blaze-slider]').forEach(observe);
}

export function initializeBlazeSlider(track) {
    return initializeTrack(track);
}

export function getBlazeSlider(track) {
    return implementation?.getBlazeSlider(track) || null;
}

export function showBlazeSlide(track, slideIndex) {
    if (implementation) return implementation.showBlazeSlide(track, slideIndex);
    void initializeTrack(track);
    return false;
}

export function refreshBlazeSlider(track) {
    if (implementation) implementation.refreshBlazeSlider(track);
}
